"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Compliance() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = [{
    number: "01",
    title: "Confidentiality & Mandatory Reporting",
    description: "We uphold strict confidentiality standards to protect family privacy, with clear exceptions for mandatory reporting as required by Arizona law. Our staff are trained to identify and report concerns regarding child safety or welfare, ensuring a secure and legally compliant environment for all participants.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_parenting_time.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_parenting_time.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_parenting_time.webp-wvc-sizes",
    alt: "Professional supervisor observing a calm family interaction"
  }, {
    number: "02",
    title: "Cancellation Policy",
    description: "To maintain consistent support for children, we require a minimum 24-hour notice for all cancellations. This policy ensures professional scheduling and protects the availability of services for all families. Detailed terms regarding late arrivals and missed sessions are provided during the intake process.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/monitored_exchange.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/monitored_exchange.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/monitored_exchange.png-wvc-sizes",
    alt: "Structured and neutral exchange process for families"
  }, {
    number: "03",
    title: "Court Documentation Procedures",
    description: "Our agency provides neutral, fact-based documentation for court proceedings. Reports are prepared with clinical precision, focusing on observable behaviors and interactions. All records are maintained in secure, encrypted systems to ensure data integrity and client confidentiality.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/observation_reporting.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/observation_reporting.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/observation_reporting.webp-wvc-sizes",
    alt: "Professional documentation and reporting for court matters"
  }];
  return <section data-section-id="2989" id="compliance-reporting" className="dark bg-background text-foreground py-32 md:py-44 lg:py-[200px] px-6 md:px-10 lg:px-16">
      <div className="max-w-[1440px] mx-auto">
        {}
        <div ref={headerRef} className={`transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase mb-8 md:mb-12" style={{
          color: "var(--chart-1)"
        }}>
            § 04 — Services
          </p>
          <h2 className="font-default font-light leading-[1.05] text-foreground max-w-[8/12] lg:max-w-[66.66%]" style={{
          fontSize: "clamp(48px, 6vw, 88px)"
        }}>
            Professional standards.
            <br />
            Neutral support.
          </h2>
        </div>

        {}
        <div ref={gridRef} className="mt-20 md:mt-28 lg:mt-[120px] grid grid-cols-1 md:grid-cols-3 relative">
          {services.map((service, i) => <div key={i} data-index={i} className={`group relative flex flex-col px-0 md:px-8 lg:px-10 py-12 md:py-0 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              {}
              {i > 0 && <div className="hidden md:block absolute left-0 top-0 bottom-0 w-px" style={{
            backgroundColor: "var(--border)"
          }} aria-hidden="true" />}
              {}
              {i > 0 && <div className="md:hidden absolute top-0 left-0 right-0 h-px" style={{
            backgroundColor: "var(--border)"
          }} aria-hidden="true" />}

              {}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{
            background: "radial-gradient(ellipse at center, var(--accent) 0%, transparent 70%)",
            mixBlendMode: "soft-light"
          }} aria-hidden="true" />

              {}
              <p className="font-mono text-xs tracking-[0.2em] mb-8 md:mb-10 relative z-10" style={{
            color: "var(--chart-1)"
          }}>
                {service.number}
              </p>

              {}
              <div className="relative overflow-hidden mb-8 md:mb-10 z-10">
                <div className="relative w-full" style={{
              paddingBottom: "133.33%"
            }}>
                  <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]" />
                </div>
              </div>

              {}
              <h3 className="font-default font-light text-foreground text-[28px] leading-tight mb-5 relative z-10">
                {service.title}
              </h3>

              {}
              <p className="font-default text-base leading-[1.7] mb-10 md:mb-12 relative z-10 flex-grow" style={{
            color: "var(--muted-foreground)"
          }}>
                {service.description}
              </p>

              {}
              <div className="relative z-10 mt-auto">
                <Link to="/OurServicesPage" className="inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase group/link" style={{
              color: "var(--chart-1)"
            }}>
                  <span className="relative">
                    Learn More
                    <span className="absolute left-0 -bottom-1 h-px w-0 group-hover:w-full transition-all duration-500 ease-out" style={{
                  backgroundColor: "var(--chart-1)"
                }} aria-hidden="true" />
                  </span>
                  <ArrowRight className="size-4 transition-transform duration-500 ease-out group-hover:translate-x-1" strokeWidth={1.5} />
                </Link>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}